import React from "react"
import { useShallow } from "zustand/react/shallow"
import { useSearchParams } from "react-router-dom"

// apis
import {
  useGetAuthoringComments,
  useFavoriteAuthoringComment,
  useResolveAuthoringComment,
} from "api/hooks/studies/useAuthoringApi"
import { useGetAuthoringReviewMetadata } from "api/hooks/studies/useAuthoringReviewApi"
import { useGetCommentTypes } from "api/hooks/admin/useCommentTypesApi"
import { useUserPermissions } from "api/hooks"
// hooks
import useAuthoringViewStore from "../stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "../stores/useAuthoringCommentsStore"
import useAuthoringStore from "../stores/useAuthoringStore"

const useAuthoringComments = () => {
  const { authoringId } = useAuthoringStore()
  const [searchParams] = useSearchParams()
  const { data: permissions } = useUserPermissions()

  const { isReviewerView } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
    }))
  )

  const { setCommentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      setCommentsMap: state.setCommentsMap,
    }))
  )

  const favoriteAuthoringComment = useFavoriteAuthoringComment(authoringId)
  const resolveAuthoringComment = useResolveAuthoringComment(authoringId)

  const projId = React.useMemo(() => {
    return searchParams.get("proj-id")
  }, [])

  const taskId = React.useMemo(() => {
    return searchParams.get("task-id")
  }, [])

  const { data: comments = [] } = useGetAuthoringComments(authoringId)
  const { data: commentTypes = [] } = useGetCommentTypes()
  const { data: authReviewData } = useGetAuthoringReviewMetadata(
    isReviewerView === true ? authoringId : null,
    isReviewerView === true ? { "project-id": projId, "actionitem-id": taskId } : {}
  )

  React.useEffect(() => {
    let map = {}

    if (Array.isArray(comments) && Array.isArray(commentTypes)) {
      let filteredCommentTypes = commentTypes?.filter(
        (type) => authReviewData?.comment_types?.findIndex((id) => id === type.id) !== -1
      )

      comments.forEach((comment) => {
        let include = true
        if (isReviewerView) {
          include = comment.tags.some((tag) => {
            const found = filteredCommentTypes?.findIndex((type) => type.name === tag) !== -1
            return found
          })
        }

        if (include) {
          if (map[comment.field_key]) {
            let comments = map[comment.field_key]
            comments.push(comment)
            map[comment.field_key] = comments
          } else {
            let comments = [comment]
            map[comment.field_key] = comments
          }
        }
      })
    }

    setCommentsMap(map)
  }, [comments, isReviewerView, authReviewData, commentTypes])

  const handleFavorite = (comment, onSuccess = () => {}) => {
    favoriteAuthoringComment(
      {
        id: `${comment.id}/favorite/`,
        body: {
          favorite: !comment.favorite,
        },
      },
      {
        onSuccess,
      }
    )
  }

  const handleResolved = (comment, onSuccess = () => {}) => {
    if (permissions["study.authoringcomment.resolve"]) {
      let updatedComment = {
        resolved: !comment.resolved,
      }

      resolveAuthoringComment(
        { id: `${comment.id}/resolve/`, body: updatedComment },
        {
          onSuccess,
        }
      )
    }
  }

  return {
    comments,
    handleFavorite,
    handleResolved,
  }
}

export default useAuthoringComments
